import React, { useState } from "react";
import "./App.css";
import axios from "axios";
import { CheckCircle, Mail, Star, Truck, Phone, Leaf, Monitor, Send, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Rating Components - All 5-star system now
const StarRating = ({ value, onChange, labels = [] }) => {
  return (
    <div className="star-rating">
      <div className="stars-container">
        {[...Array(5)].map((_, i) => (
          <button
            key={i}
            type="button"
            className={`star ${i < value ? 'active' : ''}`}
            onClick={() => onChange(i + 1)}
            aria-label={`${i + 1} von 5 Sternen`}
          >
            <Star size={28} fill={i < value ? "currentColor" : "none"} />
          </button>
        ))}
      </div>
      {labels[value - 1] && (
        <div className="rating-label">{labels[value - 1]}</div>
      )}
    </div>
  );
};

const SurveyForm = () => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    company_name: '',
    job_position: '',
    order_number: '',
    product_needs_rating: 0,
    product_quality_rating: 0,
    price_value_rating: 0,
    delivery_satisfaction_rating: 0,
    team_accessibility_rating: 0,
    improvement_suggestions: '',
    sustainability_importance: '',
    testimonial_permission: '',
    testimonial_text: '',
    testimonial_image: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // All rating labels now for 5-star system
  const needsLabels = ["Überhaupt nicht gerecht", "Eher nicht gerecht", "In etwa gerecht", "Ziemlich gerecht", "Sehr gerecht"];
  const qualityLabels = ["Sehr niedrige Qualität", "Niedrige Qualität", "Durchschnittliche Qualität", "Hohe Qualität", "Sehr hohe Qualität"];
  const priceLabels = ["Überteuert", "Kostspielig", "Durchschnittlich", "Angemessen", "Preiswert"];
  const deliveryLabels = ["Stark verzögert oder unvollständig", "Verzögert", "Leicht verzögert", "Größtenteils pünktlich", "Pünktlich & vollständig"];
  const accessibilityLabels = ["Sehr schwierig", "Schwierig", "Generell erreichbar", "Leicht", "Sehr leicht"];
  const sustainabilityLabels = ["Gar nicht wichtig", "Weniger wichtig", "Wichtig", "Sehr wichtig", "Extrem wichtig"];
  const onlineLabels = ["Sehr unzufrieden", "Unzufrieden", "Neutral", "Zufrieden", "Sehr zufrieden"];

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setError('Bitte wählen Sie ein Bilddatei (JPG, PNG oder GIF)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Die Bilddatei darf maximal 5MB groß sein');
        return;
      }
      
      setFormData({ ...formData, testimonial_image: file });
      setError(''); // Clear any previous errors
    }
  };

  const validateForm = () => {
    if (!formData.customer_name.trim()) return "Name ist erforderlich";
    if (!formData.customer_email.trim()) return "E-Mail ist erforderlich";
    if (!formData.product_quality_rating) return "Bitte bewerten Sie die Produktqualität";
    if (!formData.price_value_rating) return "Bitte bewerten Sie das Preis-Leistungs-Verhältnis";
    if (!formData.delivery_satisfaction_rating) return "Bitte bewerten Sie die Lieferung";
    if (!formData.team_accessibility_rating) return "Bitte bewerten Sie die Erreichbarkeit";
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Remove testimonial_image from submission data as backend doesn't expect it
      const { testimonial_image, ...submissionData } = formData;
      
      // Convert 0 values to null for optional rating fields to match backend validation
      if (submissionData.product_needs_rating === 0) {
        submissionData.product_needs_rating = null;
      }
      
      // Convert empty strings to null for optional fields to match backend validation
      if (submissionData.testimonial_permission === '') {
        submissionData.testimonial_permission = null;
      }
      if (submissionData.testimonial_text === '') {
        submissionData.testimonial_text = null;
      }
      if (submissionData.improvement_suggestions === '') {
        submissionData.improvement_suggestions = null;
      }
      if (submissionData.order_number === '') {
        submissionData.order_number = null;
      }
      if (submissionData.company_name === '') {
        submissionData.company_name = null;
      }
      if (submissionData.job_position === '') {
        submissionData.job_position = null;
      }
      
      const response = await axios.post(`${API}/survey/submit`, submissionData);
      
      if (response.data.success) {
        setSubmitted(true);
      } else {
        setError('Fehler beim Übermitteln der Umfrage');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError('Fehler beim Übermitteln der Umfrage. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="success-page">
        <div className="success-content">
          <CheckCircle size={80} className="success-icon" />
          <h1>Vielen Dank für Ihr <span className="success-highlight">Feedback</span>!</h1>
          <p>Ihre Umfrage wurde erfolgreich übermittelt und an unser <span className="success-highlight">Service-Team</span> gesendet.</p>
          <p>Wir schätzen Ihre Zeit und Ihr wertvolles Feedback sehr und werden Ihre Anregungen zur kontinuierlichen Verbesserung unserer Leistungen nutzen.</p>
          <button 
            className="new-survey-btn"
            onClick={() => {
              setSubmitted(false);
              setFormData({
                customer_name: '',
                customer_email: '',
                company_name: '',
                job_position: '',
                order_number: '',
                product_needs_rating: 0,
                product_quality_rating: 0,
                price_value_rating: 0,
                delivery_satisfaction_rating: 0,
                team_accessibility_rating: 0,
                improvement_suggestions: '',
                sustainability_importance: '',
                testimonial_permission: '',
                testimonial_text: '',
                testimonial_image: null
              });
            }}
          >
            Neue Umfrage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-container">
      <div className="survey-header">
        <div className="company-logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_neukunden-survey/artifacts/jcwk0oru_1_Logo_Eurosill_klein.jpg" 
            alt="Eurosill Logo" 
            className="logo-image"
          />
        </div>
        <div className="header-content">
          <h2>Kundenumfrage für Neukunden</h2>
          <p className="header-description">
            Wir möchten Ihre ersten Erfahrungen mit uns besser verstehen. Bitte beantworten Sie ein paar kurze Fragen zu Lieferung, Qualität und Service. Ihre Rückmeldung hilft uns, künftige Bestellungen noch reibungsloser abzuwickeln.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="survey-form">
        {/* Kundendaten */}
        <div className="form-section">
          <h3>
            <Mail className="section-icon" />
            Ihre Kontaktdaten
          </h3>
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => handleInputChange('customer_name', e.target.value)}
                placeholder="Ihr vollständiger Name"
                required
              />
            </div>
            <div className="form-group">
              <label>E-Mail Adresse *</label>
              <input
                type="email"
                value={formData.customer_email}
                onChange={(e) => handleInputChange('customer_email', e.target.value)}
                placeholder="ihre.email@beispiel.de"
                required
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Unternehmen</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder="Name Ihres Unternehmens"
              />
            </div>
            <div className="form-group">
              <label>Position / Funktion im Unternehmen</label>
              <input
                type="text"
                value={formData.job_position}
                onChange={(e) => handleInputChange('job_position', e.target.value)}
                placeholder="Ihre Position oder Funktion"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Bestellnummer (optional)</label>
            <input
              type="text"
              value={formData.order_number}
              onChange={(e) => handleInputChange('order_number', e.target.value)}
              placeholder="Falls Ihnen eine Bestellnummer vorliegt"
            />
          </div>
        </div>

        {/* Hauptbewertungen */}
        <div className="form-section">
          <h3>
            <Star className="section-icon" />
            Bewertung unserer Leistungen und Produkte
          </h3>
          
          <div className="rating-group">
            <label>Wie bewerten Sie die Qualität unserer gelieferten Produkte?</label>
            <StarRating
              value={formData.product_quality_rating}
              onChange={(value) => handleInputChange('product_quality_rating', value)}
              labels={qualityLabels}
            />
            <div className="rating-explanation">1 = sehr schlecht, 5 = sehr gut</div>
          </div>

          <div className="rating-group">
            <label>Wie beurteilen Sie das Preis-Leistungs-Verhältnis?</label>
            <StarRating
              value={formData.price_value_rating}
              onChange={(value) => handleInputChange('price_value_rating', value)}
              labels={priceLabels}
            />
            <div className="rating-explanation">1 = sehr schlecht, 5 = sehr gut</div>
          </div>
        </div>

        {/* Lieferung & Service */}
        <div className="form-section">
          <h3>
            <Truck className="section-icon" />
            Lieferung & Kundenservice
          </h3>
          
          <div className="rating-group">
            <label>Wie zufrieden sind Sie mit unserer Lieferung (Pünktlichkeit, Vollständigkeit)?</label>
            <StarRating
              value={formData.delivery_satisfaction_rating}
              onChange={(value) => handleInputChange('delivery_satisfaction_rating', value)}
              labels={deliveryLabels}
            />
            <div className="rating-explanation">1 = sehr schlecht, 5 = sehr gut</div>
          </div>

          <div className="rating-group">
            <label>Wie gut konnten Sie unser Team bei Rückfragen erreichen?</label>
            <StarRating
              value={formData.team_accessibility_rating}
              onChange={(value) => handleInputChange('team_accessibility_rating', value)}
              labels={accessibilityLabels}
            />
            <div className="rating-explanation">1 = sehr schlecht, 5 = sehr gut</div>
          </div>
        </div>

        {/* Verbesserungsvorschläge */}
        <div className="form-section">
          <h3>
            <Phone className="section-icon" />
            Ihre Meinung & Anregungen
          </h3>
          <div className="form-group">
            <label>Haben Sie Hinweise oder Vorschläge, wie wir unseren Service verbessern können?</label>
            <textarea
              value={formData.improvement_suggestions}
              onChange={(e) => handleInputChange('improvement_suggestions', e.target.value)}
              placeholder="Teilen Sie uns gerne mit, wie wir unseren Service verbessern können..."
              rows={4}
            />
          </div>
        </div>

        {/* Optionale Fragen */}
        <div className="form-section optional-section">
          <h3>
            <Leaf className="section-icon" />
            Zusätzliche Fragen (optional)
          </h3>
          
          <div className="rating-group">
            <label>Wie wichtig ist Ihnen Nachhaltigkeit beim Baustoffeinkauf?</label>
            <div className="checkbox-group">
              <div className="checkbox-option">
                <input
                  type="radio"
                  id="sustainability_very_important"
                  name="sustainability_importance"
                  value="very_important"
                  checked={formData.sustainability_importance === 'very_important'}
                  onChange={(e) => handleInputChange('sustainability_importance', e.target.value)}
                />
                <label htmlFor="sustainability_very_important">sehr wichtig</label>
              </div>
              <div className="checkbox-option">
                <input
                  type="radio"
                  id="sustainability_important"
                  name="sustainability_importance"
                  value="important"
                  checked={formData.sustainability_importance === 'important'}
                  onChange={(e) => handleInputChange('sustainability_importance', e.target.value)}
                />
                <label htmlFor="sustainability_important">wichtig</label>
              </div>
              <div className="checkbox-option">
                <input
                  type="radio"
                  id="sustainability_less_important"
                  name="sustainability_importance"
                  value="less_important"
                  checked={formData.sustainability_importance === 'less_important'}
                  onChange={(e) => handleInputChange('sustainability_importance', e.target.value)}
                />
                <label htmlFor="sustainability_less_important">weniger wichtig</label>
              </div>
              <div className="checkbox-option">
                <input
                  type="radio"
                  id="sustainability_not_important"
                  name="sustainability_importance"
                  value="not_important"
                  checked={formData.sustainability_importance === 'not_important'}
                  onChange={(e) => handleInputChange('sustainability_importance', e.target.value)}
                />
                <label htmlFor="sustainability_not_important">gar nicht wichtig</label>
              </div>
            </div>
          </div>

          <div className="rating-group">
            <label>
              <Monitor className="inline-icon" />
              Dürfen wir Ihre Empfehlung als Kundenstimme auf unserer Homepage veröffentlichen?
            </label>
            <div className="checkbox-group">
              <div className="checkbox-option">
                <input
                  type="radio"
                  id="testimonial_yes_name"
                  name="testimonial_permission"
                  value="yes_with_name"
                  checked={formData.testimonial_permission === 'yes_with_name'}
                  onChange={(e) => handleInputChange('testimonial_permission', e.target.value)}
                />
                <label htmlFor="testimonial_yes_name">Ja, gerne mit Namensnennung</label>
              </div>
              <div className="checkbox-option">
                <input
                  type="radio"
                  id="testimonial_yes_name_image"
                  name="testimonial_permission"
                  value="yes_with_name_and_image"
                  checked={formData.testimonial_permission === 'yes_with_name_and_image'}
                  onChange={(e) => handleInputChange('testimonial_permission', e.target.value)}
                />
                <label htmlFor="testimonial_yes_name_image">Ja, gerne mit Namensnennung + Bild</label>
              </div>
              <div className="checkbox-option">
                <input
                  type="radio"
                  id="testimonial_yes_anonymous"
                  name="testimonial_permission"
                  value="yes_anonymous"
                  checked={formData.testimonial_permission === 'yes_anonymous'}
                  onChange={(e) => handleInputChange('testimonial_permission', e.target.value)}
                />
                <label htmlFor="testimonial_yes_anonymous">Ja, aber bitte anonymisiert</label>
              </div>
              <div className="checkbox-option">
                <input
                  type="radio"
                  id="testimonial_no"
                  name="testimonial_permission"
                  value="no"
                  checked={formData.testimonial_permission === 'no'}
                  onChange={(e) => handleInputChange('testimonial_permission', e.target.value)}
                />
                <label htmlFor="testimonial_no">Nein, nicht veröffentlichen</label>
              </div>
            </div>

            {/* Image Upload Section - Only show when image option is selected */}
            {formData.testimonial_permission === 'yes_with_name_and_image' && (
              <div className="image-upload-section">
                <label className="upload-label">
                  📷 Bild für Kundenstimme hochladen
                </label>
                <div className="file-upload-area">
                  <input
                    type="file"
                    id="testimonial_image"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-input"
                  />
                  <label htmlFor="testimonial_image" className="file-upload-button">
                    {formData.testimonial_image ? (
                      <>✅ {formData.testimonial_image.name}</>
                    ) : (
                      <>📎 Bilddatei auswählen (JPG, PNG, GIF - Max. 5MB)</>
                    )}
                  </label>
                </div>
                {formData.testimonial_image && (
                  <div className="uploaded-file-info">
                    <p>📁 Datei: {formData.testimonial_image.name}</p>
                    <p>📏 Größe: {(formData.testimonial_image.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group testimonial-textarea">
            <label>Wenn Sie möchten, können Sie uns hier einen kurzen Text als Referenz für den Trust-Bereich unserer Homepage geben.</label>
            <textarea
              value={formData.testimonial_text}
              onChange={(e) => handleInputChange('testimonial_text', e.target.value)}
              placeholder="Ihr Referenztext für unsere Homepage (optional)..."
              rows={3}
            />
          </div>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            disabled={isSubmitting}
            className="submit-button"
          >
            {isSubmitting ? (
              <>
                <div className="loading-spinner"></div>
                Wird übermittelt...
              </>
            ) : (
              <>
                <Send size={20} />
                Umfrage absenden
              </>
            )}
          </button>
        </div>
      </form>

      <div className="survey-footer">
        <p>© 2025 <span className="footer-highlight">Eurosill GmbH</span> - Ihre Meinung zählt!</p>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <SurveyForm />
    </div>
  );
}

export default App;